import { ApiResListsType } from './api/OhsApiModels';
import { TierType } from './constants/OhsObject';

interface FilterPayload {
  archived: boolean;
  categories?: string[];
  workplaces?: string[];
  modules?: string[];
  viewPreset?: 'view_1' | 'view_2' | 'view_3' | 'view_4';
  sort?: {
    order: number;
    key: string;
  };
  targetTier?: {
    _id: string;
    type: TierType;
  };
}

type WithId = { _id: string };

const mergeTiers = (base: any, incoming: any) => (incoming ? { ...base, ...incoming } : base);

/**
 * Fetch all items across paginated endpoint.
 * - Pages 1..10 fetched in parallel (backend page cap scenario).
 * - After that, fetch by cursor (`next`) sequentially.
 * - Preserves original record order (page order, then cursor order).
 */
const OhsFetchAll = async <T extends WithId>(
  apiFunc: (filterInfo: any) => Promise<ApiResListsType<T[]> | null>,
  payload: FilterPayload
): Promise<ApiResListsType<T[]> | null> => {
  const fetchCountPayload = { ...payload, count: true, page: 0, next: '' };
  const countResp = (await apiFunc(fetchCountPayload)) ?? ({} as any);

  const totalPages: number = (countResp as any)?.pagination?.totalPages ?? 0;

  const items: T[] = [];
  let tiers: any = {};

  if (!totalPages) {
    return { items: [], tiers } as any;
  }

  const firstPhaseLastPage = Math.min(10, totalPages);

  // ---- Phase 1: fetch pages 1..firstPhaseLastPage concurrently, but keep ordering by page
  const pages = Array.from({ length: firstPhaseLastPage }, (_, idx) => idx + 1);

  const pagePromises = pages.map((page) =>
    apiFunc({
      ...payload,
      page,
      next: '',
      count: false,
    })
  );

  const settled = await Promise.allSettled(pagePromises);

  // Store results by page so we can flatten strictly in page order
  const pageResults = new Map<number, ApiResListsType<T[]> | null>();

  settled.forEach((res, idx) => {
    const page = pages[idx];
    if (res.status === 'fulfilled') {
      pageResults.set(page, res.value ?? null);
    } else {
      pageResults.set(page, null);
    }
  });

  // Flatten items in page order
  // eslint-disable-next-line no-restricted-syntax
  for (const page of pages) {
    const r = pageResults.get(page);
    if (r?.items?.length) items.push(...r.items);
    tiers = mergeTiers(tiers, (r as any)?.tiers);
  }

  // ---- Determine cursor seed from the REAL last page we fetched (page 10 or last page)
  const lastPageResp = pageResults.get(firstPhaseLastPage);

  // Prefer backend-provided cursor if available; otherwise fallback to last item _id.
  const getNextCursor = (resp: any): string | '' => {
    const nextFromPagination = resp?.pagination?.next;
    if (typeof nextFromPagination === 'string') return nextFromPagination;
    const lastItemId = resp?.items?.length ? resp.items[resp.items.length - 1]?._id : '';
    return lastItemId ?? '';
  };

  let nextCursor = getNextCursor(lastPageResp);

  // Infer page size if backend provides it; otherwise fallback to 100 (your old assumption)
  const pageSize: number =
    (lastPageResp as any)?.pagination?.pageSize ?? (lastPageResp as any)?.pagination?.limit ?? 100;

  // ---- Phase 2: after page 10, fetch sequentially using cursor
  if (totalPages > 10) {
    let lastBatchCount = lastPageResp?.items?.length ?? 0;

    while (nextCursor && lastBatchCount === pageSize) {
      // eslint-disable-next-line no-await-in-loop
      const res: any = await apiFunc({
        ...payload,
        page: undefined, // ensure backend doesn't treat this as a page request
        next: nextCursor,
        count: false,
      });

      const batch: T[] = res?.items ?? [];
      if (batch.length) items.push(...batch);

      tiers = mergeTiers(tiers, res?.tiers);

      lastBatchCount = batch.length;
      nextCursor = getNextCursor(res);
    }
  }

  return { items, tiers } as any;
};

export default OhsFetchAll;
